import React, { useEffect, useMemo } from 'react';
import { ErrorBoundary } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import classNames from 'classnames';
import { MediaMessageCarousel } from './components/chat/MediaMessageCarousel';
import ChatPromptWindow from './components/widget/ChatPromptWindow';
import WidgetButton from './components/widget/WidgetButton';
import WidgetOpeningManager from './components/widget/WidgetOpeningManager';
import useInit from './hooks/useInit';
import { useWidgetConfig } from './hooks/useWidgetConfig';
import { useWidgetUIStore } from './hooks/useWidgetUIStore';
import { queryClient } from './utils/queryClient';
import { getContrastColor } from './utils/style';

// Inner component that uses React Query hooks
const WidgetAppInner: React.FC = () => {
  const { isWidgetVisible } = useWidgetUIStore();

  // Fetch widget configuration - now inside QueryClientProvider
  const {
    config: widgetConfig,
    isLoading: configLoading,
    displayLogic,
  } = useWidgetConfig();

  useInit();

  const isWidgetOpenComputed = isWidgetVisible();

  const cssVariables = useMemo(() => {
    if (displayLogic.buttonColor) {
      return {
        '--sf-color-widget-background-color': `${displayLogic.buttonColor}40`, // 40% opacity
        '--sf-color-widget-button-color': displayLogic.buttonColor,
        '--sf-color-widget-button-color-dimmed': `${displayLogic.buttonColor}10`,
        '--sf-color-widget-message-background-color':
          displayLogic.messageBackgroundColor,
        '--sf-color-widget-message-text-color': getContrastColor(
          displayLogic.messageBackgroundColor,
        ),
        '--sf-color-widget-button-text-color': getContrastColor(
          displayLogic.buttonColor,
        ),
      } as React.CSSProperties;
    }
  }, [displayLogic]);

  // prevent scroll when widget is open on mobile web since its full screen
  useEffect(() => {
    if (isWidgetOpenComputed) {
      document.body.classList.add('sf:prevent-scroll');
    } else {
      document.body.classList.remove('sf:prevent-scroll');
    }
    return () => {
      document.body.classList.remove('sf:prevent-scroll');
    };
  }, [isWidgetOpenComputed]);

  return (
    <div
      id="sleekflow-widget-app"
      className={classNames(
        'sf:fixed sf:z-50 sf:bottom-0 sf:right-0',
        'sf:sm:bottom-8 sf:sm:right-8',
      )}
      style={cssVariables}
    >
      {isWidgetOpenComputed && widgetConfig && !configLoading && (
        <WidgetOpeningManager widgetConfig={widgetConfig} />
      )}

      {/* Widget Button - always visible when widget is closed */}
      {!isWidgetOpenComputed && <WidgetButton />}

      {/* Chat Prompt Window - shows welcome message above widget button when closed */}
      {!isWidgetOpenComputed && widgetConfig && !configLoading && (
        <ChatPromptWindow />
      )}

      {/* Media Message Carousel - full-screen overlay for media messages viewing */}
      <MediaMessageCarousel />
    </div>
  );
};

const WidgetApp: React.FC = () => {
  return (
    <ErrorBoundary fallback={<div>Error In Live Chat Widget</div>}>
      <QueryClientProvider client={queryClient}>
        <WidgetAppInner />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default WidgetApp;
